import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { webPage, jsonLdScript } from '@/lib/schema'

// Source of truth: uptrack-server crates/api/src/edge_areas.rs (areas and plan limits) and
// crates/alert-engine/src/nofm.rs (the verdict). Keep this page in step with them.

const AREAS: { name: string; locations: string }[] = [
  { name: 'North America East', locations: 'Toronto, Montréal' },
  { name: 'US Central', locations: 'Dallas, Chicago' },
  { name: 'North America West', locations: 'Oregon, N. California' },
  { name: 'Western Europe', locations: 'London, Paris' },
  { name: 'Central and Eastern Europe', locations: 'Nuremberg, Warsaw, Milan' },
  { name: 'South Asia', locations: 'Hyderabad, Mumbai, Delhi' },
  { name: 'Southeast Asia', locations: 'Singapore, Jakarta' },
  { name: 'East Asia', locations: 'Tokyo, Seoul' },
  { name: 'South America', locations: 'São Paulo, Rio de Janeiro' },
  { name: 'Oceania', locations: 'Sydney, Melbourne' },
  { name: 'Middle East', locations: 'Doha, Tel Aviv' },
  { name: 'Africa', locations: 'Johannesburg, Cape Town' },
]

const TITLE = 'Check Locations: Areas, Limits and Alert Rules | Uptrack'
const DESCRIPTION =
  'The 24 Uptrack check locations in 12 areas, how many each plan can use, how often each city checks, and what must fail before you get an alert.'
const CANONICAL = 'https://uptrack.app/docs/check-locations'

export const Route = createFileRoute('/_site/docs_/check-locations')({
  component: CheckLocationsPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: 'Uptrack check locations: areas, limits and alert rules' },
      { property: 'og:description', content: DESCRIPTION },
    ],
    links: [{ rel: 'canonical', href: CANONICAL }],
    scripts: [jsonLdScript(webPage({ name: TITLE, description: DESCRIPTION, url: CANONICAL }))],
  }),
})

const H2 = 'font-sans mb-4 mt-10 text-2xl font-semibold'
const P = 'mb-4 text-lg leading-[1.7] text-body'
const LI = 'text-lg leading-[1.7] text-body'

function CheckLocationsPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <h1 className="font-sans mb-4 text-4xl font-semibold">Check locations</h1>
      <p className={P}>
        Uptrack has 24 check locations in 12 areas. Each area has two Cloudflare cities. Two areas also have a core probe, in
        Nuremberg and Hyderabad. The core probes run every monitor type. HTTP and keyword monitors can use the cities and
        the core probes. Ping, port, DNS and SSL checks run only from the core probes.
      </p>

      <h2 className={H2}>Areas</h2>
      <table className="font-sans mb-4 w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="py-2 pr-4 font-semibold">Area</th>
            <th className="py-2 font-semibold">Locations</th>
          </tr>
        </thead>
        <tbody>
          {AREAS.map((a) => (
            <tr key={a.name} className="border-b border-border">
              <td className="py-2 pr-4">{a.name}</td>
              <td className="py-2 text-body">{a.locations}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className={H2}>What each plan can use</h2>
      <ul className="mb-4 list-disc space-y-2 pl-6">
        <li className={LI}>Pro: any 6 areas, which is 12 locations, plus any core probe in those areas. You pick them for each monitor.</li>
        <li className={LI}>Team and Business: all 12 areas, which is 24 locations, plus the 2 core probes.</li>
      </ul>
      <p className={P}>
        Each monitor keeps at least 2 areas, so a second area can confirm a failure. You can also run a monitor on Cloudflare
        cities only, with no core probe.
      </p>

      <h2 className={H2}>How often each location checks</h2>
      <p className={P}>
        The two cities of an area take turns, so the area checks your site at every interval and each city checks at every
        second interval. A core probe checks at every interval.
      </p>

      <h2 className={H2}>What must fail before you get an alert</h2>
      <ul className="mb-4 list-disc space-y-2 pl-6">
        <li className={LI}>A location that fails checks again once before its failure counts.</li>
        <li className={LI}>An area fails when every location that answered in it failed.</li>
        <li className={LI}>Three picked areas must fail, or every picked area when you pick fewer than three.</li>
        <li className={LI}>The failures must continue for the number of checks in your confirmation setting, at least 2.</li>
        <li className={LI}>When a core probe in your picks answers and every such probe is up, city failures alone do not alert.</li>
      </ul>
      <p className={P}>
        A site that fails in only one or two areas is a regional problem. It shows on the monitor, and you can turn on a
        separate regional notice for each monitor.
      </p>

      <h2 className={H2}>When a location cannot give an answer</h2>
      <p className={P}>
        A location that cannot run a check, is blocked by your site or is throttled does not count as a failure. It counts
        as no answer. Each Cloudflare city also checks two control sites in every batch. When a city fails them both, its
        failures are ignored for that batch.
      </p>
      <p className={P}>
        This has a limit. A monitor with no core probe has no independent check on Cloudflare. A Cloudflare fault that
        passes the control sites and fails your site could send an alert. Keep at least one core area if you want that
        check.
      </p>

      <h2 className={H2}>Allow Uptrack through your firewall</h2>
      <p className={P}>
        City checks come from Cloudflare Workers, so they use addresses that Cloudflare shares with its other customers.
        To let them through, add a custom header with a long secret value to your monitor, and allow requests that carry
        it.
      </p>
      <p className={P}>
        City checks also carry the header <code className="font-mono text-base">CF-Worker: uptrack.app</code>. It helps you
        find them in your logs. Do not use it as your only allow rule, because any client can send it to a site that
        Cloudflare does not protect. The bot details are on the{' '}
        <Link className="text-primary hover:underline" to="/bot">
          Uptrack bot page
        </Link>
        .
      </p>

      <div className="font-sans mt-12 rounded-lg border border-border bg-muted p-6 text-center">
        <h2 className="mb-2 text-2xl font-semibold">Check from where your users are</h2>
        <p className="mx-auto mb-6 max-w-lg text-base text-body">
          Plans start from $3/mo, with a 30-day money-back guarantee.
        </p>
        <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
          <Link to="/pricing">See plans</Link>
        </Button>
      </div>
    </article>
  )
}
